// src/views/Liquidaciones.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';
import * as XLSX from 'xlsx';
import { BANCOS } from '../utils/bankData';
import { formatMoney } from '../utils/utils';


/* ─── helpers ────────────────────────────────────────────────── */
const fmtFecha = (str) => {
    if (!str) return '—';
    const [fecha, hora] = str.split(' ');
    const [y, m, d] = fecha.split('-');
    return `${d}-${m}-${y}${hora ? ' ' + hora.slice(0, 5) : ''}`;
};

const fmtPeriodo = (str) => {
    if (!str) return '—';
    const [y, m] = str.split('-');
    const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return `${meses[parseInt(m, 10) - 1]} ${y}`;
};

/* ─── badge de estado ─────────────────────────────────────────── */
function EstadoBadge({ estado }) {
    if (!estado || estado === 'pendiente')
        return <span className="badge bg-secondary">Pendiente</span>;
    if (estado === 'borrador')
        return <span className="badge bg-warning text-dark"><i className="bi bi-pencil me-1"/>Borrador</span>;
    if (estado === 'confirmado')
        return <span className="badge bg-success"><i className="bi bi-check-circle me-1"/>Emitido</span>;
    return <span className="badge bg-light text-dark">{estado}</span>;
}

/* ─── botones de acción ───────────────────────────────────────── */
function BotonesAccion({ periodo, tipo, estado, estadoAdelanto, onAction, onExport, loading }) {
    const navigate = useNavigate();
    const tipoLabel = tipo === 'adelanto' ? 'Adelanto' : 'FM';
    const adelantoOk = tipo === 'fm' ? estadoAdelanto === 'confirmado' : true;

    return (
        <div className="dropdown">
            <button
                className="btn btn-sm btn-secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={loading}
            >
                Acciones {tipoLabel}
            </button>
            <ul className="dropdown-menu">
                {(!estado || estado === 'pendiente') && (
                    <li>
                        <button
                            className="dropdown-item text-primary"
                            onClick={() => onAction(periodo, tipo === 'adelanto' ? 'adelanto' : 'fin_mes', 'generar')}
                            disabled={!adelantoOk}
                        >
                            <i className="bi bi-lightning me-1"/> Liquidar {tipoLabel}
                        </button>
                    </li>
                )}
                {estado === 'borrador' && (
                    <>
                        <li>
                            <button
                                className="dropdown-item text-warning"
                                onClick={() => onAction(periodo, tipo === 'adelanto' ? 'adelanto' : 'fin_mes', 'generar')}
                            >
                                <i className="bi bi-arrow-repeat me-1"/> Reliquidar {tipoLabel}
                            </button>
                        </li>
                        <li>
                            <button
                                className="dropdown-item text-success"
                                onClick={() => onAction(periodo, tipo === 'adelanto' ? 'adelanto' : 'fin_mes', 'confirmar')}
                            >
                                <i className="bi bi-check-circle me-1"/> Emitir {tipoLabel}
                            </button>
                        </li>
                    </>
                )}
                {estado === 'confirmado' && (
                    <li>
                        <button
                            className="dropdown-item text-info"
                            onClick={() => navigate(`/liquidacion/${periodo}?tipo=${tipo === 'adelanto' ? 'adelanto' : 'fin_mes'}`)}
                        >
                            <i className="bi bi-eye me-1"/> Ver detalle {tipoLabel}
                        </button>
                    </li>
                )}
                <li className="dropdown-divider"></li>
                <li>
                    <button
                        className="dropdown-item text-success"
                        onClick={() => onExport(periodo, tipo)}
                    >
                        <i className="bi bi-file-earmark-excel me-1"/> Nómina Pago {tipoLabel}
                    </button>
                </li>
            </ul>
        </div>
    );
}


/* ─── componente principal ─────────────────────────────────────── */
function Liquidaciones() {
    const navigate = useNavigate();
    const [periodos, setPeriodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [accionLoading, setAccionLoading] = useState(false);
    const [msg, setMsg] = useState(null); // { tipo: 'success'|'danger', texto }

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await axiosInstance.get('/liquidacion/lista');
            setPeriodos(resp.data.data || []);
        } catch (e) {
            console.error(e);
            setMsg({ tipo: 'danger', texto: 'Error al cargar los períodos de liquidación.' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const handleAccion = async (periodo, tipo, accion) => {
        const labels = { generar: 'generar', confirmar: 'emitir' };
        const tipoLabel = tipo === 'adelanto' ? 'Adelanto' : 'Fin de Mes';

        if (accion === 'confirmar') {
            if (!window.confirm(`¿Emitir y bloquear el ${tipoLabel} del período ${periodo}? Esta acción no se puede deshacer.`)) return;
        }

        setAccionLoading(true);
        setMsg(null);
        try {
            if (accion === 'generar') {
                const resp = await axiosInstance.post('/liquidacion/generar', { periodo, tipo });
                const body = resp.data;
                if (body.error) throw new Error(body.message);
                setMsg({ tipo: 'success', texto: body.message });
            } else {
                const resp = await axiosInstance.post('/liquidacion/confirmar', { periodo, tipo });
                const body = resp.data;
                if (body.error) throw new Error(body.message);
                setMsg({ tipo: 'success', texto: body.message });
            }
            await cargar();
        } catch (e) {
            setMsg({ tipo: 'danger', texto: e.message || 'Error al ejecutar la acción.' });
        } finally {
            setAccionLoading(false);
        }
    };

    const handleExportarNominaPago = async (periodo, tipo) => {
        setAccionLoading(true);
        try {
            // Reutilizamos el endpoint de remuneraciones
            const params = { fecha: periodo };
            if (tipo === 'adelanto') params.adelanto = true;

            const response = await axiosInstance.get("/acuerdo-mutuo/new-remuneraciones", { params });
            const resultados = response.data.data || [];

            if (!resultados.length) {
                alert("No hay datos para exportar");
                return;
            }

            const [anio] = periodo.split("-");
            const anioInt = parseInt(anio, 10);
            const retencionSii = anioInt >= 2026 ? 0.8475 : 0.855;

            const dataToExport = resultados.map(row => {
                let totalAPagar = 0;
                if (tipo === 'adelanto') {
                    if (row.anticipo !== 1) return null;
                    const brutoTurnos = row.valor_turnos_adelanto || 0;
                    const horasExtras = row.horas_extras_adelanto || 0;
                    const movilizacion = row.locomocion_adelanto || 0;
                    const colacion = row.colacion_adelanto || 0;
                    const prestamos = row.prestamos_adelanto || 0;
                    const otrosSuma = row.otros_suma_adelanto || 0;
                    const otrosResta = row.otros_resta_adelanto || 0;
                    const vacaciones = row.vacaciones_adelanto || 0;

                    totalAPagar = Math.round((brutoTurnos + horasExtras) * retencionSii + movilizacion + colacion + otrosSuma - otrosResta + vacaciones - prestamos);
                } else {
                    const turnosAdelanto = parseFloat(row.valor_turnos_adelanto || 0);
                    const extrasAdelanto = parseFloat(row.horas_extras_adelanto || 0);
                    const movAdelanto = parseFloat(row.locomocion_adelanto || 0);
                    const colAdelanto = parseFloat(row.colacion_adelanto || 0);
                    const liquidoAnticipo = ((turnosAdelanto + extrasAdelanto) * retencionSii) + movAdelanto + colAdelanto;

                    const turnosTotal = parseFloat(row.valor_turnos_total || 0);
                    const extrasTotal = parseFloat(row.horas_extras_total || 0);
                    const movTotal = parseFloat(row.locomocion_total || 0);
                    const colTotal = parseFloat(row.colacion_total || 0);
                    const liquidoTotalMes = (turnosTotal + extrasTotal) * retencionSii + movTotal + colTotal - liquidoAnticipo;

                    const prestamoFM = parseFloat(row.prestamos_fm || 0);
                    const otrosSumaFM = parseFloat(row.otros_suma_fm || 0);
                    const otrosRestaFM = parseFloat(row.otros_resta_fm || 0);
                    const vacacionesFM = parseFloat(row.vacaciones_fm || 0);

                    totalAPagar = Math.round(liquidoTotalMes - prestamoFM + otrosSumaFM - otrosRestaFM + vacacionesFM);
                }

                if (totalAPagar <= 0) return null;

                const cleanText = (str) => {
                    if (!str) return "";
                    return str
                        .toString()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "") // Eliminar acentos
                        .replace(/[^a-zA-Z0-9 ]/g, "")   // Dejar solo letras, números y espacios
                        .trim();
                };

                const cleanAccount = (str) => {
                    if (!str) return "";
                    return str.toString().replace(/\D/g, ""); // Solo números
                };

                let rutBody = "", rutDv = "";
                const rutBeneficiario = row.rut_pago || row.rut;
                if (rutBeneficiario) {
                    const cleanRut = rutBeneficiario.replace(/\./g, "").replace(/-/g, "");
                    rutBody = cleanRut.slice(0, -1);
                    rutDv = cleanRut.slice(-1);
                }

                // Usar datos estructurados del backend
                const nombrePago = row.nombre_pago || "";
                const banco = row.banco || "";
                const tipoCuentaBancaria = row.tipo_cuenta_bancaria || "";
                const numeroCuenta = cleanAccount(row.numero_cuenta || "");

                // Buscar código de banco por nombre
                let codigoBanco = "";
                if (banco) {
                    const bancoUpper = banco.toUpperCase();
                    let bancoFound = BANCOS.find(b => bancoUpper.includes(b.nombre.toUpperCase()));

                    if (!bancoFound) {
                        const getCodigoBanco = (text) => {
                            if (!text) return "";
                            if (text.includes("CHILE") || text.includes("EDWARDS")) return "001";
                            if (text.includes("ESTADO") || text.includes("CUENTA RUT") || text.includes("CUENTARUT")) return "012";
                            if (text.includes("SANTANDER")) return "037";
                            if (text.includes("BCI") || text.includes("CREDITO") || text.includes("CRÉDITO") || text.includes("MACH")) return "016";
                            if (text.includes("BICE")) return "028";
                            if (text.includes("FALABELLA")) return "051";
                            if (text.includes("ITAU") || text.includes("ITAÚ")) return "039";
                            if (text.includes("SCOTIA") || text.includes("SCOTIABANK")) return "014";
                            if (text.includes("RIPLEY") || text.includes("CHEK")) return "053";
                            if (text.includes("SECURITY")) return "049";
                            if (text.includes("CONSORCIO")) return "055";
                            if (text.includes("INTERNACIONAL")) return "009";
                            if (text.includes("HSBC")) return "031";
                            if (text.includes("COOPEUCH")) return "672";
                            if (text.includes("TENPO")) return "730";
                            if (text.includes("MERCADO") || text.includes("MERCADOPAGO")) return "875";
                            if (text.includes("HEROES") || text.includes("HÉROES")) return "729";
                            if (text.includes("TAPP") || text.includes("ANDES")) return "732";
                            if (text.includes("COPEC")) return "741";
                            if (text.includes("PARIS")) return "057";
                            if (text.includes("POLAR")) return "697";
                            if (text.includes("BRASIL")) return "017";
                            if (text.includes("BBVA")) return "504";
                            return "";
                        };
                        const codigoMejorado = getCodigoBanco(bancoUpper);
                        if (codigoMejorado) {
                            bancoFound = { codigo: codigoMejorado };
                        }
                    }

                    if (bancoFound) codigoBanco = bancoFound.codigo;
                }

                // Determinar medio de pago según tipo de cuenta y banco
                let medioPago = 7;
                const esVistaOAhorro = tipoCuentaBancaria && (
                    tipoCuentaBancaria.toUpperCase().includes("VISTA") ||
                    tipoCuentaBancaria.toUpperCase().includes("RUT") ||
                    tipoCuentaBancaria.toUpperCase().includes("AHORRO")
                );

                if (codigoBanco === "001") {
                    // Banco de Chile
                    // El usuario solicita que VISTA sea 1, no 6.
                    const esVista = tipoCuentaBancaria && tipoCuentaBancaria.toUpperCase().includes("VISTA");
                    if (esVistaOAhorro && !esVista) {
                        medioPago = 6; 
                    } else {
                        medioPago = 1; 
                    }
                } else {
                    medioPago = 7;
                }

                const rawNombreBeneficiario = nombrePago || `${row.nombre_auxiliar || ""} ${row.apepat_auxiliar || ""} ${row.apemat_auxiliar || ""}`;
                const nombreBeneficiario = cleanText(rawNombreBeneficiario);

                const rawNombreAuxiliar = `${row.nombre_auxiliar || ""} ${row.apepat_auxiliar || ""} ${row.apemat_auxiliar || ""}`;
                const nombreAuxiliarClean = cleanText(rawNombreAuxiliar);

                const descripcionPago = cleanText(`Pago ${nombreAuxiliarClean} ${periodo} ${tipo === 'adelanto' ? 'Adelanto' : 'FM'}`);
                const mensajePago = cleanText(`Periodo ${periodo} ${tipo === 'adelanto' ? 'Adelanto' : 'FM'}`);

                // Alertas de validación EXACTAS a Honorarios.js
                const alertas = [];
                if (!codigoBanco && medioPago !== 2 && medioPago !== 12 && medioPago !== 4) alertas.push("FALTA BANCO");
                if (!numeroCuenta && medioPago !== 2 && medioPago !== 12 && medioPago !== 4) alertas.push("FALTA CUENTA");
                if (!rutBody) alertas.push("DATO RUT");

                const alertaStr = alertas.length > 0 ? `REVISAR: ${alertas.join(", ")}` : "";
                
                return {
                    "ALERTA DATOS": alertaStr,
                    "MEDIO DE PAGO*": medioPago,
                    "RUT BENEFICIARIO*": rutBody,
                    "DÍGITO VERIFICADOR RUT BENEFICIARIO*": rutDv,
                    "NOMBRE BENEFICIARIO*": nombreBeneficiario.slice(0, 60),
                    "CÓDIGO BANCO*": codigoBanco,
                    "NÚMERO CUENTA*": numeroCuenta,
                    "MONTO PAGO*": totalAPagar,
                    "DESCRIPCIÓN PAGO": descripcionPago.slice(0, 119),
                    "MAIL DE DESTINO PARA AVISO": row.email || row.email_auxiliar || "",
                    "MENSAJE": mensajePago.slice(0, 256)
                };
            }).filter(Boolean);

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);

            // Ajustar anchos de columna
            const cols = Object.keys(dataToExport[0] || {}).map(key => ({ wch: key.length + 8 }));
            worksheet["!cols"] = cols;

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "NominaPago");
            XLSX.writeFile(workbook, `Nomina_Pagos_${tipo.toUpperCase()}_${periodo}.xlsx`);
        } catch (error) {
            console.error(error);
            alert("Error al generar nómina");
        } finally {
            setAccionLoading(false);
        }
    };



    return (
        <div className="container-fluid py-4 px-4">

            {/* Encabezado */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-receipt fs-3 text-primary"/>
                    <div>
                        <h4 className="mb-0 fw-bold">Liquidaciones de Honorarios</h4>
                        <small className="text-muted">Gestión de períodos — Adelanto y Fin de Mes</small>
                    </div>
                </div>
                <button className="btn btn-outline-secondary btn-sm" onClick={cargar} disabled={loading}>
                    <i className="bi bi-arrow-clockwise me-1"/>{loading ? 'Actualizando…' : 'Actualizar'}
                </button>
            </div>

            {/* Mensaje de retroalimentación */}
            {msg && (
                <div className={`alert alert-${msg.tipo} alert-dismissible d-flex align-items-center gap-2`}>
                    <i className={`bi bi-${msg.tipo === 'success' ? 'check-circle' : 'exclamation-triangle'}`}/>
                    <span>{msg.texto}</span>
                    <button className="btn-close ms-auto" onClick={() => setMsg(null)}/>
                </div>
            )}

            {/* Tabla de períodos */}
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 fw-semibold text-secondary">
                        <i className="bi bi-calendar3 me-2"/>Períodos de liquidación
                    </h6>
                    <span className="badge bg-primary rounded-pill">{periodos.length} período{periodos.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status"/>
                            <div className="mt-2 text-muted small">Cargando períodos…</div>
                        </div>
                    ) : periodos.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-1 d-block mb-2"/>
                            No hay períodos de liquidación registrados.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">Período</th>
                                        <th>Rango</th>
                                        <th className="text-center" style={{ borderLeft: '2px solid #dee2e6' }}>
                                            Estado Adelanto
                                        </th>
                                        <th className="text-center">Fecha liquidación Adelanto</th>
                                        <th className="text-center">Acciones Adelanto</th>
                                        <th className="text-center" style={{ borderLeft: '2px solid #dee2e6' }}>
                                            Estado FM
                                        </th>
                                        <th className="text-center">Fecha liquidación FM</th>
                                        <th className="text-center">Acciones FM</th>
                                        <th className="text-center">Ver completo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {periodos.map(p => (
                                        <tr key={p.id}>
                                            {/* Período */}
                                            <td className="ps-3 fw-semibold">{fmtPeriodo(p.periodo)}</td>

                                            {/* Rango */}
                                            <td className="small text-muted text-nowrap">
                                                {fmtFecha(p.fecha_desde)} — {fmtFecha(p.fecha_hasta)}
                                            </td>

                                            {/* Adelanto */}
                                            <td className="text-center" style={{ borderLeft: '2px solid #dee2e6' }}>
                                                <EstadoBadge estado={p.estado_adelanto}/>
                                            </td>
                                            <td className="text-center small text-muted text-nowrap">
                                                {fmtFecha(p.fecha_liquidacion_adelanto)}
                                            </td>
                                            <td className="text-center">
                                                <BotonesAccion
                                                    periodo={p.periodo}
                                                    tipo="adelanto"
                                                    estado={p.estado_adelanto}
                                                    estadoAdelanto={p.estado_adelanto}
                                                    onAction={handleAccion}
                                                    onExport={handleExportarNominaPago}
                                                    loading={accionLoading}
                                                />
                                            </td>


                                            {/* FM */}
                                            <td className="text-center" style={{ borderLeft: '2px solid #dee2e6' }}>
                                                <EstadoBadge estado={p.estado_fm}/>
                                            </td>
                                            <td className="text-center small text-muted text-nowrap">
                                                {fmtFecha(p.fecha_liquidacion_fm)}
                                            </td>
                                            <td className="text-center">
                                                <BotonesAccion
                                                    periodo={p.periodo}
                                                    tipo="fm"
                                                    estado={p.estado_fm}
                                                    estadoAdelanto={p.estado_adelanto}
                                                    onAction={handleAccion}
                                                    onExport={handleExportarNominaPago}
                                                    loading={accionLoading}
                                                />
                                            </td>


                                            {/* Ver completo */}
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => navigate(`/liquidacion/${p.periodo}`)}
                                                    title="Ver período completo"
                                                >
                                                    <i className="bi bi-folder2-open"/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Leyenda de estados */}
            <div className="mt-3 d-flex flex-wrap gap-3 text-muted small">
                <span><span className="badge bg-secondary me-1">Pendiente</span>No generado aún</span>
                <span><span className="badge bg-warning text-dark me-1">Borrador</span>Generado, pendiente emitir</span>
                <span><span className="badge bg-success me-1">Emitido</span>Bloqueado y definitivo</span>
                <span><i className="bi bi-info-circle me-1 text-primary"/>FM solo se puede liquidar si el Adelanto está emitido</span>
            </div>
        </div>
    );
}

export default Liquidaciones;
